import moment from "moment";
import { useReducer } from "react";
import { IOrderQuery } from "../../models/order/query";
import { IOrderDetails } from "../../models/order/response";
import EndPoints from "../../services/end-points";
import { execute } from "../../utils/helpers/execute";
import OrderContext, { internalState } from "./context";
import reducer from "./reducer";
import {
  IExportOrderExcel,
  IMarkAccountingFinishedRequest,
  Update,
} from "../../models/order/request";
import { IUpdateOrderCustomerSatisfactionRequest } from "../../models/customer-satisfaction/request";
export interface IProps {
  children: React.ReactNode;
}
const OrderContextProvider: React.FC<IProps> = (props) => {
  const [state, dispatch] = useReducer(reducer, internalState);
  const getData = async () => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "list" } });

        const { data } = await EndPoints.order.getAllOrders(state.query);

        dispatch({ type: "SET_LIST", payload: { list: data } });
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "list" } });
      },
      throwException: false,
    });
  };
  const getDataShort = async () => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "list" } });

        const { data } = await EndPoints.order.getAllOrdersShort(state.query);

        dispatch({ type: "SET_LIST_SHORT", payload: { listShort: data } });
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "list" } });
      },
      throwException: false,
    });
  };

  const getDataRated = async () => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "list" } });

        const { data } = await EndPoints.order.getRatedOrders(state.query);

        dispatch({ type: "SET_LIST_RATED", payload: { listRated: data } });
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "list" } });
      },
      throwException: false,
    });
  };

  const getDetails = async (id: number) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "details" } });

        const { data } = await EndPoints.order.getOrder(id);
        dispatch({ type: "SET_DETAILS", payload: { details: data } });
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "details" } });
      },
      throwException: false,
    });
  };

  const updateOrder = async (id: number, request: Update) => {
    await execute({
      callback: async () => {
        dispatch({
          type: "LOADING",
          payload: { loading: "update" },
        });
        await EndPoints.order.updateOrder(id, request);

        getData();
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({
          type: "LOADING",
          payload: { loading: "update" },
        });
      },
      throwException: true,
    });
  };

  const setDetails = async (data?: IOrderDetails) => {
    dispatch({ type: "SET_DETAILS", payload: { details: data } });
  };

  const setSearch = (search?: string) => {
    dispatch({ type: "SET_SEARCH", payload: { search } });
  };

  const setQuery = (query: IOrderQuery) => {
    dispatch({ type: "SET_QUERY", payload: { query } });
  };
  const exportExcel = async (request: IExportOrderExcel) => {
    await execute({
      callback: async () => {
        dispatch({
          type: "LOADING",
          payload: { loading: "create" },
        });
        const data = await EndPoints.order.exportExcel(
          {
            ...request,
            filters: state.query.filters ?? [],
          },
          state.query,
        );
        const url = window.URL.createObjectURL(new Blob([data.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `Orders-${moment().format("yyyy-MM-d")}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({
          type: "LOADING",
          payload: { loading: "create" },
        });
      },
      throwException: true,
    });
  };

  const confirmPayment = async (id: number) => {
    await execute({
      callback: async () => {
        dispatch({
          type: "LOADING",
          payload: { loading: "confirm_payment" },
        });
        const { data } = await EndPoints.order.confirmPayment(id);

        dispatch({ type: "SET_DETAILS", payload: { details: data } });
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({
          type: "LOADING",
          payload: { loading: "confirm_payment" },
        });
      },
      throwException: true,
    });
  };

  const markAccountingFinished = async (
    id: number,
    request: IMarkAccountingFinishedRequest,
  ) => {
    await execute({
      callback: async () => {
        dispatch({
          type: "LOADING",
          payload: { loading: "mark_accounting_finished" },
        });
        const { data } = await EndPoints.order.markAccountingFinished(
          id,
          request,
        );

        dispatch({ type: "SET_DETAILS", payload: { details: data } });
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({
          type: "LOADING",
          payload: { loading: "mark_accounting_finished" },
        });
      },
      throwException: true,
    });
  };

  const updateCustomerSatisfaction = async (
    id: number,
    request: IUpdateOrderCustomerSatisfactionRequest,
  ) => {
    await execute({
      callback: async () => {
        dispatch({
          type: "LOADING",
          payload: { loading: "customer_satisfaction" },
        });
        await EndPoints.order.updateCustomerSatisfaction(id, request);
        const { data } = await EndPoints.order.getOrder(id);

        dispatch({ type: "SET_DETAILS", payload: { details: data } });
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({
          type: "LOADING",
          payload: { loading: "customer_satisfaction" },
        });
      },
      throwException: true,
    });
  };

  return (
    <OrderContext.Provider
      value={{
        ...state,
        actions: {
          getData,
          getDataShort,
          getDataRated,
          getDetails,
          setDetails,
          updateOrder,
          confirmPayment,
          markAccountingFinished,
          updateCustomerSatisfaction,

          exportExcel,

          setSearch,
          setQuery,
        },
      }}
    >
      {props.children}
    </OrderContext.Provider>
  );
};

export default OrderContextProvider;
